import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  DateValue,
  addToast,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { useForm } from "@mantine/form";
import { getLocalizedName, getLocalizedValue } from "@/utils";
import dayjs from "dayjs";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { parseDate } from "@internationalized/date";
import { api, queryClient } from "@/lib/api-client";
import { useEffect } from "react";
import { GetCustomerSubscriptions } from "@/server-types";

type VisitEventFormValues = {
  date: DateValue;
  time: string;
  serviceId?: number;
  duration: number;
  customerSubscriptionId?: number;
  locationId?: number;
};

type VisitEventModalProps = {
  isOpen: boolean;
  onClose: () => void;
  customerId: number;
};

export const VisitEventModal = ({
  isOpen,
  onClose,
  customerId,
}: VisitEventModalProps) => {
  const { data: customer } = api.customer.useGetCustomerNew(customerId);

  const { mutateAsync: postCustomerSubscriptionVisitEvent } =
    api.customer.usePostCustomerSubscriptionVisitEvent();

  const { data: customerSubscriptionsResponse } =
    api.customer.useGetCustomerSubscriptions({
      customerId,
      status: "active",
    });

  const customerSubscriptions =
    customerSubscriptionsResponse?.customerSubscriptionsWithUsage ?? [];

  const {
    data: locationsResponse = {
      locations: [],
    },
  } = api.location.useGetLocations({
    sortBy: "labelAsc",
  });

  const form = useForm<VisitEventFormValues>({
    initialValues: {
      date: parseDate(dayjs().format("YYYY-MM-DD")),
      time: dayjs().format("HH:mm"),
      serviceId: undefined,
      customerSubscriptionId: undefined,
      duration: 30,
      locationId: undefined,
    },
    validate: {
      serviceId: (value) => (value ? undefined : t`Izberite storitev`),
      customerSubscriptionId: (value) =>
        value ? undefined : t`Izberite naročnino`,
      locationId: (value) => (value ? undefined : t`Izberite lokacijo`),
    },
  });

  useEffect(() => {
    if (customerSubscriptions.length === 0) return;
    if (locationsResponse.locations.length === 0) return;

    form.setValues({
      customerSubscriptionId: customerSubscriptions[0].id,
      serviceId:
        customerSubscriptions[0].customerSubscriptionServices[0]?.id ||
        undefined,
      duration: 30,
      locationId: locationsResponse.locations[0].locationId,
    });
  }, [customerSubscriptions, locationsResponse]);

  if (!customer) return null;

  const subscriptionOptions = getSubscriptionOptions(customerSubscriptions);

  const selectedSubscription = customerSubscriptions.find(
    (customerSubscription) =>
      customerSubscription.id ===
      Number(form.getValues().customerSubscriptionId),
  );

  const serviceOptions = selectedSubscription
    ? getServiceOptions(selectedSubscription)
    : [];

  const locationOptions = locationsResponse.locations.map((location) => ({
    label: location.label,
    key: location.locationId.toString(),
  }));

  const handleSubmit = async (values: VisitEventFormValues) => {
    if (
      !values.serviceId ||
      !values.customerSubscriptionId ||
      !values.locationId
    ) {
      addToast({
        title: t`Napaka`,
        description: t`Izberite vse potrebne podatke`,
        color: "danger",
      });
      return;
    }
    const startTime = `${dayjs(values.date.toString()).format("YYYY-MM-DD")}T${values.time}`;

    try {
      await postCustomerSubscriptionVisitEvent({
        customerSubscriptionId: values.customerSubscriptionId,
        body: {
          locationId: values.locationId,
          startTime,
          serviceId: values.serviceId,
          duration: values.duration,
          customerId,
        },
      });

      addToast({
        title: t`Obisk zabeležen`,
        color: "success",
      });
      onClose();
    } catch (e) {
      console.log(e);
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri beleženju obiska`,
        color: "danger",
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["customer-subscription", customerId],
    });
    form.reset();
  };

  return (
    <LimeModal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>{t`Zabeleži obisk`}</ModalHeader>
        <form onSubmit={form.onSubmit(handleSubmit)} autoComplete="off">
          <ModalBody>
            <LimeDatePicker
              label={t`Datum`}
              value={form.values.date}
              {...form.getInputProps("date")}
              onChange={(value) => {
                if (!value) return;
                form.setFieldValue("date", value);
              }}
            />
            <LimeInput
              id="visit-time"
              type="time"
              className="w-full"
              label={t`Čas`}
              {...form.getInputProps("time")}
            />
            <LimeSelect
              label={t`Naročnina`}
              items={subscriptionOptions}
              {...form.getInputProps("customerSubscriptionId")}
              value={form.values.customerSubscriptionId?.toString()}
              onChange={(value: string) => {
                form.setFieldValue("customerSubscriptionId", Number(value));
                const selected = customerSubscriptions.find(
                  (sub) => sub.id.toString() === value,
                );
                form.setFieldValue(
                  "serviceId",
                  selected?.customerSubscriptionServices[0].id,
                );
              }}
            />
            <LimeSelect
              label={t`Storitev`}
              items={serviceOptions}
              {...form.getInputProps("serviceId")}
              value={form.values.serviceId?.toString()}
              onChange={(value: string) => {
                form.setFieldValue("serviceId", Number(value));
              }}
            />
            <LimeInput
              id="visit-duration"
              type="number"
              className="w-full"
              label={t`Trajanje (min)`}
              min={1}
              {...form.getInputProps("duration")}
            />
            <LimeSelect
              label={t`Lokacija`}
              items={locationOptions}
              {...form.getInputProps("locationId")}
              value={form.values.locationId?.toString()}
              onChange={(value: string) => {
                form.setFieldValue("locationId", Number(value));
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" type="button" onPress={onClose}>
              {t`Prekliči`}
            </Button>
            <Button variant="solid" type="submit">
              {t`Zabeleži obisk`}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </LimeModal>
  );
};

const getSubscriptionOptions = (
  customerSubscriptions: GetCustomerSubscriptions["response"]["customerSubscriptionsWithUsage"],
) =>
  customerSubscriptions.map((customerSubscription) => ({
    label: customerSubscription.subscription
      ? getLocalizedValue(customerSubscription.subscription?.nameLocalized)
      : t`Naročnina po meri`,
    key: customerSubscription.id.toString(),
  }));

const getServiceOptions = (
  selectedSubscription: GetCustomerSubscriptions["response"]["customerSubscriptionsWithUsage"][number],
) => {
  if (!selectedSubscription.customerSubscriptionServices) {
    return [];
  }

  return selectedSubscription.customerSubscriptionServices.map(
    (customerSubscriptionService: {
      service: {
        nameLocalized: {
          serviceId: number;
          name: string;
          id: number;
          language: string;
        }[];
      };
      serviceId: number;
      id: number;
    }) => ({
      label: getLocalizedName(
        customerSubscriptionService.service.nameLocalized,
      ),
      key: customerSubscriptionService.serviceId.toString(),
    }),
  );
};
